import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Lock, Sparkles, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface UpgradePromptProps {
  feature: string;
  description: string;
  minPlan?: string;
  className?: string;
  compact?: boolean;
}

export function UpgradePrompt({ 
  feature, 
  description, 
  minPlan = "Perso",
  className = "",
  compact = false
}: UpgradePromptProps) {
  const navigate = useNavigate();

  if (compact) {
    return (
      <div className={`flex items-center gap-3 p-3 bg-muted/50 rounded-lg border border-border/50 ${className}`}>
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Lock className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{feature}</p>
          <p className="text-xs text-muted-foreground truncate">{description}</p>
        </div>
        <Button size="sm" onClick={() => navigate("/pricing")} className="shrink-0">
          Upgrade
        </Button>
      </div>
    );
  }

  return (
    <Card className={`border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 ${className}`}>
      <CardContent className="p-6 text-center space-y-4">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <Lock className="w-8 h-8 text-primary" />
        </div>
        
        <div className="space-y-2">
          <h3 className="text-lg font-semibold flex items-center justify-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            {feature}
          </h3>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            {description}
          </p>
        </div>

        <div className="pt-2">
          <p className="text-xs text-muted-foreground mb-3">
            Disponible à partir du plan <span className="font-medium text-foreground">{minPlan}</span>
          </p>
          <Button onClick={() => navigate("/pricing")} className="gap-2">
            Voir les plans
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface LimitReachedProps {
  type: "documents" | "ai_searches" | "pdf_actions" | "share_links" | "storage";
  current: number;
  limit: number;
  className?: string;
}

export function LimitReached({ type, current, limit, className = "" }: LimitReachedProps) {
  const navigate = useNavigate();

  const typeLabels: Record<string, { name: string; unit: string }> = {
    documents: { name: "Documents", unit: "documents" },
    ai_searches: { name: "Recherches IA", unit: "recherches" },
    pdf_actions: { name: "Actions PDF", unit: "actions" },
    share_links: { name: "Liens de partage", unit: "liens" },
    storage: { name: "Stockage", unit: "MB" },
  };

  const { name, unit } = typeLabels[type] || { name: type, unit: "" };

  return (
    <Card className={`border-destructive/30 bg-destructive/5 ${className}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
          <Lock className="w-6 h-6 text-destructive" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-destructive">Limite {name} atteinte</h4>
          <p className="text-sm text-muted-foreground">
            Vous avez utilisé {current}/{limit} {unit}. Passez à un plan supérieur pour continuer.
          </p>
        </div>

        <Button 
          variant="destructive" 
          size="sm" 
          onClick={() => navigate("/pricing")}
          className="shrink-0"
        >
          Upgrade
        </Button>
      </CardContent>
    </Card>
  );
}
